<template>
  <section
    ref="elRef"
    class="FutureRunTimelineView"
    data-testid="futureRunTimeline"
    :aria-busy="isFetching"
  >
    <Transition>
      <EmptyState
        v-if="hasFetched && futureRuns.length === 0"
        class="FutureRunTimelineView__emptyState"
        data-testid="futureRunTimelineEmptyState"
        iconName="calendarX"
        :title="currentRuns.length > 0 ? 'No more upcoming runs' : 'No upcoming runs'"
        :subtitle="`This might be a good time\nto work on your game`"
      />
      <RunTimeline
        v-else
        class="FutureRunTimelineView__timeline"
        :runs="futureRuns"
      />
    </Transition>
  </section>
</template>

<script lang="ts" setup>
import EmptyState from "@/components/EmptyState.vue";
import RunTimeline from "@/components/RunTimeline.vue";
import useRunCarouselStore from "@/stores/useRunCarouselStore";
import { storeToRefs } from "pinia";
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";

const {
  futureRuns,
  currentRuns,
} = storeToRefs(useRunCarouselStore());

const {
  fetchFutureRuns,
} = useRunCarouselStore();

const elRef = useTemplateRef<HTMLDivElement>("elRef");
const isFetching = ref(false);
const hasFetched = ref(false);

const intersectionObserver = new IntersectionObserver(handleIntersection, {
  threshold: 0.5,
});

onMounted(() => {
  if (elRef.value) intersectionObserver.observe(elRef.value);
});

onBeforeUnmount(() => {
  intersectionObserver.disconnect();
});

async function handleIntersection (entries: Array<IntersectionObserverEntry>) {
  if (isFetching.value) return;

  const entry = entries.find((entry) => entry.isIntersecting);
  if (!entry) return;

  isFetching.value = true;
  const [error] = await fetchFutureRuns();
  isFetching.value = false;

  if (!error) hasFetched.value = true;
}
</script>

<style lang="scss" scoped>
.FutureRunTimelineView {
  position: relative;
  height: 100%;
  min-height: 400px;
}

.FutureRunTimelineView__timeline,
.FutureRunTimelineView__emptyState {
  transition: 350ms opacity;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}

.FutureRunTimelineView__emptyState {
  position: absolute;
  inset: 0;

  border: solid 2px var(--themeDarkTransparent100);
  border-radius: 6px;
}
</style>
